import type { DocumentSection, Validation } from "@/api/schemas";
import type { StatusKind } from "@/components/ui/badge-status";
import { countHr } from "@/lib/pluralHr";

export interface SectionStatus {
  kind: StatusKind;
  label: string;
  tooltip: string;
}

/**
 * Per-section validation status — same logic as the legacy
 * ``perSectionStatus`` (W6) but with a stable type. Errors win over
 * warnings; warnings win over OK.
 */
export function perSectionStatus(
  sectionId: string,
  validation: Validation,
): SectionStatus {
  const errors = validation.errors.filter((e) => e.section_id === sectionId);
  const warnings = validation.warnings.filter((w) => w.section_id === sectionId);
  if (errors.length) {
    return {
      kind: "err",
      label: countHr(errors.length, "greška", "greške", "grešaka"),
      tooltip: errors.map((e) => e.message).join("\n"),
    };
  }
  if (warnings.length) {
    return {
      kind: "warn",
      label: `${warnings.length} upoz.`,
      tooltip: warnings.map((w) => w.message).join("\n"),
    };
  }
  return { kind: "ok", label: "OK", tooltip: "Validacija uspješna" };
}

export function ifcGroundedFactKeys(
  section: DocumentSection,
  factSheet: Record<string, unknown>,
): string[] {
  if (section.source !== "llm" && section.source !== "llm_refined") return [];
  const used = section.facts_used ?? [];
  const sources = (factSheet?._sources as Record<string, string> | undefined) ?? {};
  return used.filter((k) => sources[k] === "ifc");
}

/** S3 — klasa B (standardni stručni tekst, puna LLM sloboda) nosi amber
 * oznaku „generirano — pregledaj": sigurnosni sloj je potpisnik, ne softver,
 * pa UI mora jasno reći koji tekst NIJE izveden iz činjenica projekta.
 * Klase A (činjenice) i C (proza sidrena na FactSheet) ne nose oznaku. */
export function contentClassBadge(
  section: DocumentSection,
): { label: string; tooltip: string } | null {
  if (section.content_class !== "B") return null;
  return {
    label: "generirano — pregledaj",
    tooltip:
      "Standardni stručni tekst sastavljen automatski — pregledaj i " +
      "prilagodi projektu prije potpisa. Brojke i imena projekta smiju " +
      "dolaziti samo iz podataka projekta.",
  };
}

export function sourceTagLabel(source: DocumentSection["source"]): string {
  switch (source) {
    case "template":
      return "Predložak";
    case "llm":
      return "AI";
    case "llm_refined":
      return "AI dorađeno";
    case "placeholder":
      return "Projektant";
    default:
      return "Standardno";
  }
}

export function sourceTagClass(source: DocumentSection["source"]): string {
  switch (source) {
    case "template":
      return "bg-stone-100 text-stone-600 border-stone-200";
    case "llm":
      return "bg-ind-50 text-ind-700 border-ind-200";
    case "llm_refined":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "placeholder":
      return "bg-orange-100 text-orange-800 border-orange-200";
    default:
      return "bg-stone-100 text-stone-500 border-stone-200";
  }
}
